/*
 * Attachment links in article text (2026-10-28, ENHANCEMENTS S47):
 *
 *     [[attachment:9b1d4c2e-...]]                       link, text = the file name
 *     [[attachment:9b1d4c2e-...|the installation guide]] with explicit link text
 *
 * The target is the attachment's ID and nothing else. An attachment belongs to
 * a library and may be used by many articles, so its file name is neither
 * unique nor stable - two products may each have an "Installation guide.pdf",
 * and replacing the file may rename it - while the id survives both. The
 * editor inserts the token; nobody is expected to type a GUID.
 *
 * The token's grammar is [[kb:...]]'s (articleLinks.ts), and so is its
 * treatment of code: a token inside a code span or block is text, so an article
 * can show the syntax. The two share nothing else - an attachment link resolves
 * against the article's own attachments, never against the article model.
 *
 * WHAT A LINK MEANS FOR WHO MAY DOWNLOAD. A token in an article's CURRENT text
 * is one of the two ways a reader of that article may download the attachment -
 * the article's list (kb.ArticleAttachment) is the other. The server reads the
 * text with this module to decide, so the parse here and the parse there are
 * the same parse.
 *
 * Unresolved - an id no attachment has any more, or a malformed target - is
 * rendered as its link text, or as nothing visible where there is none, exactly
 * as an unresolvable [[kb:...]] token is: a sentence that still reads, never a
 * raw token and never a dead link.
 */
import { escapeMarkdownText, encodeMarkdownHref, isGuid, maskMarkdownCode, sanitiseTokenText } from './articleLinks.js';
export const ATTACHMENT_LINK_PREFIX = '[[attachment:';
/** [[kb:...]]'s TOKEN_PATTERN with the other prefix: bounded, no newlines, no nesting. */
const TOKEN_PATTERN = /\[\[attachment:([^\]|\r\n]+?)(?:\|([^\]\r\n]*?))?\]\]/g;
/** The token for an attachment, as the editor inserts it. */
export function buildAttachmentLinkToken(attachmentId, text) {
    const label = text ? `|${sanitiseTokenText(text)}` : '';
    return `${ATTACHMENT_LINK_PREFIX}${attachmentId.toLowerCase()}${label}]]`;
}
/**
 * Every attachment token in a markdown document, outside code, each with its
 * offset. The id is lower-cased, or null when the target is not a GUID - such a
 * token is still returned, so that it can be rendered as plain text rather than
 * left raw.
 */
export function parseAttachmentLinkTokens(markdown) {
    if (!markdown || !markdown.includes(ATTACHMENT_LINK_PREFIX))
        return [];
    const masked = maskMarkdownCode(markdown);
    const tokens = [];
    TOKEN_PATTERN.lastIndex = 0;
    let match;
    while ((match = TOKEN_PATTERN.exec(masked)) !== null) {
        const index = match.index;
        const target = (match[1] ?? '').trim();
        const text = match[2]?.trim();
        tokens.push({
            token: markdown.slice(index, index + match[0].length),
            index,
            attachmentId: isGuid(target) ? target.toLowerCase() : null,
            ...(text ? { text } : {}),
        });
    }
    return tokens;
}
/** The distinct attachment ids a document links, lower case. */
export function collectAttachmentLinkIds(markdown) {
    const ids = new Set();
    for (const token of parseAttachmentLinkTokens(markdown)) {
        if (token.attachmentId)
            ids.add(token.attachmentId);
    }
    return [...ids];
}
/**
 * Substitutes the resolved tokens for markdown links, by offset and back to
 * front as renderArticleLinks does, and every other token for its plain text.
 * A resolution whose token no longer stands at its offset is skipped.
 */
export function renderAttachmentLinks(markdown, resolutions, options) {
    if (resolutions.length === 0)
        return markdown;
    const ordered = [...resolutions].sort((a, b) => b.index - a.index);
    let output = markdown;
    for (const resolution of ordered) {
        if (output.slice(resolution.index, resolution.index + resolution.token.length) !== resolution.token) {
            continue;
        }
        const label = resolution.text ?? resolution.fileName ?? '';
        const replacement = resolution.resolved && resolution.attachmentId
            ? `[${escapeMarkdownText(label)}](${encodeMarkdownHref(options.buildHref(resolution.attachmentId))})`
            : escapeMarkdownText(label);
        output = output.slice(0, resolution.index) + replacement + output.slice(resolution.index + resolution.token.length);
    }
    return output;
}
/**
 * Resolves every token of a document against the attachments the reader may
 * reach through it - the server's half of rendering, done where it knows the
 * files. An id the map does not hold is unresolved.
 */
export function resolveAttachmentLinks(markdown, attachments) {
    return parseAttachmentLinkTokens(markdown).map((token) => {
        const attachment = token.attachmentId ? attachments.get(token.attachmentId) : undefined;
        return attachment
            ? {
                ...token,
                resolved: true,
                fileName: attachment.fileName,
                byteLength: attachment.byteLength,
                contentType: attachment.contentType,
            }
            : { ...token, resolved: false };
    });
}
/**
 * The file names of the resolved links, by id - markdownToPlainText's
 * attachmentNames, for plain text that reads as the rendered page does.
 */
export function attachmentLinkNames(resolutions) {
    const names = new Map();
    for (const resolution of resolutions) {
        if (resolution.resolved && resolution.attachmentId && resolution.fileName) {
            names.set(resolution.attachmentId, resolution.fileName);
        }
    }
    return names;
}
//# sourceMappingURL=attachmentLinks.js.map